import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {InputFormat, WizardDataService} from "../../../services/wizard-data.service";
import {jsonValidator} from "../../../validators/json.validator";
import {MatTab, MatTabGroup} from "@angular/material/tabs";
import {NgForOf, NgIf, UpperCasePipe} from "@angular/common";
import {MatError} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatButton} from "@angular/material/button";
import {MatButtonToggle, MatButtonToggleGroup} from "@angular/material/button-toggle";

@Component({
  selector: 'app-step-two-files',
  standalone: true,
    imports: [
        MatTabGroup,
        ReactiveFormsModule,
        UpperCasePipe,
        MatError,
        MatTab,
        MatInput,
        MatButton,
        NgIf,
        NgForOf,
        MatButtonToggleGroup,
        FormsModule,
        MatButtonToggle
    ],
  templateUrl: './step-two-files.component.html',
  styleUrl: './step-two-files.component.scss'
})
export class StepTwoFilesComponent implements OnInit {
    @Output() completed = new EventEmitter<void>();
    @Output() back = new EventEmitter<void>();

    inputFormat: InputFormat = 'separate';
    separateFilesForm: FormGroup;
    combinedFileForm: FormGroup;

    constructor(
        private _formBuilder: FormBuilder,
        public wizardData: WizardDataService
    ) {}

    ngOnInit(): void {
        this.inputFormat = this.wizardData.inputFormat;
        this.buildSeparateFilesForm();
        this.buildCombinedFileForm();
    }

    private buildSeparateFilesForm(): void {
        this.separateFilesForm = this._formBuilder.group({
            baseJson: new FormControl(this.wizardData.baseJsonString, jsonValidator()),
        });

        this.wizardData.targetLanguages.forEach((lang) => {
            const existingData = this.wizardData.targetJsonMap.get(lang);
            const jsonString = existingData ? JSON.stringify(existingData, null, 2) : '';
            this.separateFilesForm.addControl(lang, new FormControl(jsonString, jsonValidator()));
        });
    }

    private buildCombinedFileForm(): void {
        this.combinedFileForm = this._formBuilder.group({
            combinedJson: new FormControl('', jsonValidator()),
        });
    }

    handleFileInput(event: Event, form: FormGroup, controlName: string): void {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) return;

        const file = input.files[0];
        const reader = new FileReader();

        reader.onload = (e: ProgressEvent<FileReader>) => {
            const text = e.target?.result as string;
            form.get(controlName)?.setValue(text);
            form.get(controlName)?.markAsTouched();
        };

        reader.readAsText(file);
        input.value = '';
    }

    isFormInvalid(): boolean {
        return this.inputFormat === 'separate'
            ? this.separateFilesForm.invalid
            : this.combinedFileForm.invalid;
    }

    onNext(): void {
        if (this.isFormInvalid()) return;

        this.wizardData.inputFormat = this.inputFormat;

        if (this.inputFormat === 'separate') {
            this.processSeparateFiles();
        } else {
            this.processCombinedFile();
        }

        this.completed.emit();
    }

    private processSeparateFiles(): void {
        const baseJsonValue = this.separateFilesForm.get('baseJson')?.value;
        this.wizardData.baseJsonString = baseJsonValue;
        this.wizardData.baseJson = JSON.parse(baseJsonValue);

        this.wizardData.targetJsonMap.clear();
        this.wizardData.targetLanguages.forEach((lang) => {
            const langJsonValue = this.separateFilesForm.get(lang)?.value;
            this.wizardData.targetJsonMap.set(lang, langJsonValue ? JSON.parse(langJsonValue) : {});
        });
    }

    private processCombinedFile(): void {
        const combinedJsonValue = this.combinedFileForm.get('combinedJson')?.value;
        const combinedObject = JSON.parse(combinedJsonValue);

        const baseLangKey = this.wizardData.baseLanguage;
        this.wizardData.baseJson = combinedObject[baseLangKey] || {};
        this.wizardData.baseJsonString = JSON.stringify(this.wizardData.baseJson, null, 2);

        this.wizardData.targetJsonMap.clear();
        this.wizardData.targetLanguages.forEach((lang) => {
            this.wizardData.targetJsonMap.set(lang, combinedObject[lang] || {});
        });
    }

    onBack(): void {
        this.back.emit();
    }
}
